import { useEffect, useMemo, useState, useCallback } from 'react';
import { X, Search, FileText, AlertTriangle, Send, CheckCircle2, Paperclip } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import {
  extractTemplateText,
  type TemplateVariables,
  type ExtractedTemplateText,
} from '@/lib/services/whatsapp/templateRendering';
import {
  describeTemplate,
  fieldValue,
  headerNeedsMedia,
  renderTemplatePlainText,
  templateBlockReason,
  HEADER_MEDIA_ID_KEY,
  HEADER_MEDIA_LINK_KEY,
  type TemplateShape,
} from '@/lib/services/whatsapp/templateSend';

export interface SelectedTemplatePayload {
  templateName: string;
  language: string;
  /**
   * Valori delle variabili, chiavi di templateSend.ts: 'body.1', 'header.1',
   * 'body.nome_cliente', 'button.0.1', e per l'allegato dell'intestazione
   * 'header.media_id' (file gia' caricato su Meta) o 'header.media_link'.
   * Vanno passati cosi' come sono a provider.sendTemplate({ variables }).
   */
  variables: TemplateVariables;
  /**
   * Il testo che il cliente legge, con le variabili sostituite. Serve a salvare
   * in chat il messaggio vero invece del nome tecnico del template.
   */
  renderedText?: string;
}

interface DbTemplate {
  id: string;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: string;
  components: unknown[];
  rejection_reason: string | null;
}

interface Props {
  isOpen: boolean;
  configId: string;
  /** Valori auto-fill (es. {{1}}=nome contatto). Vuoto se non si vuole pre-compilare. */
  prefillVariables?: TemplateVariables;
  onClose: () => void;
  /** Invocato quando l'operatore clicca "Invia". Il composer chiamerà provider.sendTemplate. */
  onSend: (payload: SelectedTemplatePayload) => void | Promise<void>;
}

type CategoryFilter = 'ALL' | 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

/**
 * Modal di selezione template HSM da inviare a un contatto.
 *
 * Mostra SOLO template APPROVED del config attivo. Layout split:
 *  - sx: lista filtrabile (search + category chip)
 *  - dx: preview con variabili evidenziate (rosso=mancanti, verde=risolte) + input
 *
 * Bottone "Invia" abilitato solo quando tutte le variabili required sono risolte.
 *
 * Niente integrazione provider qui: il modal emette `onSend(payload)`, è il
 * composer (Cloud-aware) che chiama `provider.sendTemplate()` e gestisce l'errore.
 */
export function TemplateSelectorModal({ isOpen, configId, prefillVariables, onClose, onSend }: Props) {
  const [templates, setTemplates] = useState<DbTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [variables, setVariables] = useState<TemplateVariables>(prefillVariables || {});
  const [submitting, setSubmitting] = useState(false);
  // Allegato dell'intestazione (modelli con immagine/video/documento): file dal
  // computer (caricato su Meta al momento dell'invio) oppure link https pubblico.
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const provider = useWhatsAppChatStore((s) => s.provider);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    supabase
      .from('whatsapp_templates')
      .select('id, name, language, category, status, components, rejection_reason')
      .eq('config_id', configId)
      .eq('status', 'APPROVED')
      .order('updated_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) {
          setError(err.message);
          setTemplates([]);
        } else {
          setTemplates((data || []) as DbTemplate[]);
        }
        setLoading(false);
      });
  }, [isOpen, configId]);

  // Reset selezione + variabili quando si chiude/apre
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setSearch('');
      setCategory('ALL');
    } else {
      setVariables(prefillVariables || {});
    }
  }, [isOpen, prefillVariables]);

  // Cambio modello: l'allegato e l'errore precedente non valgono piu'.
  useEffect(() => {
    setHeaderFile(null);
    setSendError(null);
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (category !== 'ALL' && t.category !== category) return false;
      if (!q) return true;
      const parts = extractTemplateText(t.components);
      const hay = [t.name, parts.body, parts.header, parts.footer, ...parts.buttons].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [templates, search, category]);

  const selected = useMemo(
    () => filtered.find((t) => t.id === selectedId) || null,
    [filtered, selectedId],
  );

  const parts: ExtractedTemplateText | null = useMemo(
    () => (selected ? extractTemplateText(selected.components) : null),
    [selected],
  );

  // Struttura per l'invio: variabili di intestazione/corpo/pulsanti separate,
  // anche a nome ({{nome_cliente}}), e allegato dell'intestazione.
  const shape: TemplateShape | null = useMemo(
    () => (selected ? describeTemplate(selected.components) : null),
    [selected],
  );

  // Per il controllo "pronto all'invio" un file scelto conta gia' come allegato
  // (viene caricato su Meta solo al clic su Invia). Il valore precompilato
  // '1' (nome del contatto) vale per {{1}} di corpo e intestazione finche'
  // l'operatore non lo cambia.
  const effectiveVars = useMemo(() => {
    const out: TemplateVariables = { ...variables };
    if (headerFile) out[HEADER_MEDIA_ID_KEY] = 'da-caricare';
    return out;
  }, [variables, headerFile]);

  const blockReason = shape ? templateBlockReason(shape, effectiveVars) : null;
  const ready = !!shape && !blockReason;
  const needsMedia = !!shape && headerNeedsMedia(shape);

  const handleSend = useCallback(async () => {
    if (!selected || !shape || !ready || submitting) return;
    setSubmitting(true);
    setSendError(null);
    try {
      const vars: TemplateVariables = { ...variables };
      if (needsMedia && headerFile) {
        if (!provider?.uploadMediaFile) throw new Error("Caricamento dell'allegato non disponibile su questo numero.");
        vars[HEADER_MEDIA_ID_KEY] = await provider.uploadMediaFile(headerFile, headerFile.name);
        delete vars[HEADER_MEDIA_LINK_KEY];
      }
      await onSend({
        templateName: selected.name,
        language: selected.language,
        variables: vars,
        renderedText: renderTemplatePlainText(shape, vars),
      });
      onClose();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Caricamento dell'allegato non riuscito.");
    } finally {
      setSubmitting(false);
    }
  }, [selected, shape, ready, submitting, onSend, variables, onClose, needsMedia, headerFile, provider]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-4xl h-[80vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Invia template</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Solo template approvati da Meta. {templates.length} disponibili.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Chiudi">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Lista sx */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col min-w-0">
            <div className="p-3 space-y-2 border-b border-gray-100 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca per nome o testo..."
                  className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
              </div>
              <div className="flex gap-1.5 text-xs">
                {(['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION'] as CategoryFilter[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                      category === c
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {c === 'ALL' ? 'Tutti' : c === 'MARKETING' ? 'Marketing' : c === 'UTILITY' ? 'Servizio' : 'Auth'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && <p className="text-xs text-gray-400 p-4">Caricamento template...</p>}
              {error && <p className="text-xs text-red-500 p-4">{error}</p>}
              {!loading && !error && filtered.length === 0 && (
                <p className="text-xs text-gray-400 p-4">Nessun template approvato corrisponde ai filtri.</p>
              )}
              {filtered.map((t) => {
                const partsT = extractTemplateText(t.components);
                const preview = partsT.body.slice(0, 80) + (partsT.body.length > 80 ? '…' : '');
                const isSelected = selectedId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    onDoubleClick={() => { setSelectedId(t.id); if (ready) handleSend(); }}
                    className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">{t.name}</span>
                      <span className="text-[10px] uppercase font-semibold text-gray-400">{t.language}</span>
                    </div>
                    {preview && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 pl-5">{preview}</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview dx */}
          <div className="w-1/2 flex flex-col min-w-0">
            {!selected || !parts ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-6">
                Seleziona un template per la preview
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {needsMedia && shape && (
                    <div className="p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-2">
                      <p className="text-[10px] uppercase font-semibold text-gray-400">
                        Allegato dell'intestazione ({shape.headerFormat === 'IMAGE' ? 'immagine' : shape.headerFormat === 'VIDEO' ? 'video' : 'documento'})
                      </p>
                      <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Paperclip className="h-3.5 w-3.5" />
                        {headerFile ? headerFile.name : 'Scegli un file'}
                        <input
                          type="file"
                          className="hidden"
                          accept={shape.headerFormat === 'IMAGE' ? 'image/jpeg,image/png' : shape.headerFormat === 'VIDEO' ? 'video/mp4,video/3gpp' : 'application/pdf'}
                          onChange={(e) => setHeaderFile(e.target.files?.[0] ?? null)}
                        />
                      </label>
                      {!headerFile && (
                        <input
                          type="url"
                          value={variables[HEADER_MEDIA_LINK_KEY] || ''}
                          onChange={(e) => setVariables({ ...variables, [HEADER_MEDIA_LINK_KEY]: e.target.value })}
                          placeholder="oppure incolla un link https al file"
                          className="w-full px-2 py-1 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        />
                      )}
                    </div>
                  )}
                  {shape?.headerFormat === 'LOCATION' && (
                    <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200">
                      Questo modello ha una posizione nell'intestazione e non si puo' inviare dalla chat.
                    </div>
                  )}
                  {parts.header && (
                    <PreviewBlock label="Intestazione" text={parts.header} component="header" variables={variables} />
                  )}
                  <PreviewBlock label="Corpo" text={parts.body} component="body" variables={variables} bold />
                  {parts.footer && (
                    <PreviewBlock label="Piè di pagina" text={parts.footer} component="footer" variables={variables} muted />
                  )}
                  {parts.buttons.length > 0 && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1.5">Bottoni</p>
                      <div className="flex flex-wrap gap-1.5">
                        {parts.buttons.map((b, i) => (
                          <span key={i} className="px-2 py-1 rounded-md text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {shape && shape.fields.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <p className="text-[10px] uppercase font-semibold text-gray-400">Variabili</p>
                      {shape.fields.map((f) => (
                        <div key={f.key} className="flex items-center gap-2">
                          <label className="text-xs font-mono text-gray-500 min-w-12 max-w-[45%] truncate" title={f.label}>{f.label}</label>
                          <input
                            type="text"
                            value={fieldValue(variables, f)}
                            onChange={(e) => setVariables({ ...variables, [f.key]: e.target.value })}
                            placeholder="(vuoto)"
                            className="flex-1 px-2 py-1 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer azioni */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between gap-3">
                  {sendError ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-300">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {sendError}
                    </span>
                  ) : blockReason ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {blockReason}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Pronto all'invio
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={!ready || submitting}
                    onClick={handleSend}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? 'Invio…' : 'Invia template'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PreviewBlockProps {
  label: string;
  text: string;
  /** Componente del modello: le variabili dell'intestazione sono separate da quelle del corpo. */
  component: 'header' | 'body' | 'footer';
  variables: TemplateVariables;
  bold?: boolean;
  muted?: boolean;
}

/**
 * Renderizza testo con highlight: `{{N}}` non risolti in rosso, risolti in verde.
 * Resto del testo normale.
 */
function PreviewBlock({ label, text, component, variables, bold, muted }: PreviewBlockProps) {
  const parts: { text: string; resolved?: boolean }[] = [];
  let lastIdx = 0;
  const re = new RegExp(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ text: text.slice(lastIdx, m.index) });
    const val = component === 'footer'
      ? ''
      : fieldValue(variables, { key: `${component}.${m[1]}`, component, name: m[1], label: '' });
    parts.push({ text: val && val.length > 0 ? val : m[0], resolved: !!(val && val.length > 0) });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ text: text.slice(lastIdx) });

  const baseCls = muted ? 'text-xs text-gray-500 dark:text-gray-400' : 'text-sm text-gray-800 dark:text-gray-200';
  const weight = bold ? 'font-medium' : '';

  return (
    <div>
      <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1">{label}</p>
      <p className={`${baseCls} ${weight} whitespace-pre-wrap break-words`}>
        {parts.map((p, i) => {
          if (p.resolved === undefined) return <span key={i}>{p.text}</span>;
          const cls = p.resolved
            ? 'px-1 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
            : 'px-1 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 font-mono text-[11px]';
          return <span key={i} className={cls}>{p.text}</span>;
        })}
      </p>
    </div>
  );
}

// Testo semplice del template con le variabili sostituite: e' quello che si salva in chat.
// Mantenuto per compatibilita': ora il calcolo vive in templateSend.renderTemplatePlainText.
export function renderToPlainText(parts: ExtractedTemplateText, variables: TemplateVariables): string {
  const components = [
    parts.header ? { type: 'HEADER', format: 'TEXT', text: parts.header } : null,
    { type: 'BODY', text: parts.body },
    parts.footer ? { type: 'FOOTER', text: parts.footer } : null,
  ].filter(Boolean);
  return renderTemplatePlainText(describeTemplate(components), variables);
}
